/** @jsx jsx */

import { pickBy } from 'lodash';
import { useQueryClient } from 'react-query';
import { Fragment, useEffect, useRef, useState } from 'react';

import { usePrevious } from '@zapier/common-hooks';
import {
  Modal,
  ModalContent,
  SkeletonText,
  Spacer,
  Text,
} from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

import InputEditor from 'app/developer-v3/components/InputEditor';
import SaveButtonGroup from 'app/developer-v3/components/SaveButtonGroup';
import Link from 'app/common/components/Link';
import { getTeamUrl } from 'app/developer-v3/utils/url/url';
import {
  useNotify,
  useQuerySelfSignupDomains,
  useUpdateSelfSignupDomains,
} from 'app/developer-v3/hooks';

const Styles = {
  buttonWrapper: css`
    display: flex;
    justify-content: flex-end;
    padding: 30px 30px 0px;
    position: sticky;
    bottom: 0;
    background: white;
    border-top: 1px solid rgba(0, 0, 0, 0.2);
    margin: 0 -30px;
    box-shadow: 0 30px 0 0 white;
    z-index: 1;
  `,
  labelWrapper: css`
    min-width: 80px;
  `,
};

type Domain = string;
type ErrorMessage = string;

type Props = {
  appId: string;
  onClose: () => void;
};

function SettingsModal({ appId, onClose }: Props) {
  const bottomElemRef = useRef<HTMLDivElement>();

  const [formDomains, setFormDomains] = useState<Domain[]>([]);
  // An array of objects. Each item has a email domain  as the key and error
  // message as the value.
  const [formDomainErrors, setFormDomainErrors] = useState<
    Record<Domain, ErrorMessage>
  >({});

  const notify = useNotify();
  const queryClient = useQueryClient();

  const {
    domains: savedDomains,
    isLoading: isQuerying,
    queryKey,
  } = useQuerySelfSignupDomains(appId);
  const savedDomainsPrevious = usePrevious(savedDomains);

  const { isMutating, mutate: updateDomains } = useUpdateSelfSignupDomains(
    appId,
    {
      onError: (error: any) => {
        const { errors } = JSON.parse(error.responseText);
        const errorDomains = errors?.[0]?.detail?.allowed_self_signup_domains;

        // Response could be a string or an object.
        if (typeof errorDomains === 'string') {
          notify.failure('Unable to update email domains.');
        } else {
          setFormDomainErrors(
            Object.fromEntries(
              Object.entries(
                errorDomains
              ).map(([indexString, errorMessages]) => [
                formDomains[indexString],
                errorMessages[0],
              ])
            )
          );
        }
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries(queryKey);
        notify.success('Email domains updated!');
        onClose();
      },
    }
  );

  const scrollToBottom = () =>
    bottomElemRef.current?.scrollIntoView?.({ behavior: 'smooth' });

  useEffect(() => {
    // Scroll to bottom when form is updated.
    scrollToBottom();
  }, [formDomains]);

  useEffect(() => {
    // Update the form when queried domains are changed.
    if (
      !isMutating &&
      !isQuerying &&
      savedDomains.toString() !== (savedDomainsPrevious ?? []).toString()
    ) {
      setFormDomains(savedDomains);
    }
  }, [isMutating, isQuerying, savedDomains, savedDomainsPrevious]);

  return (
    <Modal onClosed={onClose}>
      <ModalContent>
        <Text
          margin="0px 0px 40px 0px"
          tag="h1"
          textAlign="center"
          type="SectionHeader"
        >
          Email domains team members can use to join
        </Text>
        <Text>
          Anyone with an email address associated with an approved email domain
          can join your integration as a{' '}
          <Link href={`${getTeamUrl(appId)}/members`} target="_blank">
            Collaborator
          </Link>{' '}
          on their own, without waiting for an admin invitation.
        </Text>
        <Spacer height={30} />
        <Text tag="p" type="Body4">
          Approved email domains
        </Text>
        <Spacer height={10} />
        <Text>
          Enter <Text type="Body3">acme.com</Text> if{' '}
          <Text type="Body3">john.smith@acme.com</Text> can join your
          integration.
        </Text>
        <Spacer height={20} />
        {isQuerying && isMutating ? (
          <SkeletonText lines={3} size="heading" />
        ) : (
          <Fragment>
            <InputEditor
              inputName="e.g. acme.com"
              items={formDomains.map(d => ({
                value: d,
                isInvalidValue: d in formDomainErrors,
                error: formDomainErrors?.[d],
              }))}
              onUpdate={itemsNext => {
                const formDomainsNext = itemsNext.map(
                  itemNext => itemNext.value
                );
                const formDomainErrorsNext = pickBy(
                  formDomainErrors,
                  (_errorMsg, errorEmail) =>
                    formDomainsNext.includes(errorEmail)
                );
                setFormDomains(formDomainsNext);
                setFormDomainErrors(formDomainErrorsNext);
              }}
            />
            <Spacer height={30} />
            <div css={Styles.buttonWrapper}>
              <SaveButtonGroup
                isSaving={isMutating}
                onSave={() => updateDomains({ domains: formDomains })}
              />
            </div>
          </Fragment>
        )}
        <div ref={bottomElemRef} />
      </ModalContent>
    </Modal>
  );
}

export default SettingsModal;
